import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, useParams, Navigate, Link, useNavigate } from 'react-router-dom';

// Expose React + ReactDOM globally — original prototype components read
// `const { useState } = React` and `window.X` patterns. Setting these BEFORE
// dynamic imports below ensures they're available when component modules
// register themselves on window.
window.React = React;
window.ReactDOM = ReactDOM;

async function boot() {
  // Data files self-register on window: WORKPLACES, FUNNELS, AVATARS, getFunnel, ALL_PAGES, buildFields, FAQS, etc.
  await import('./data.js');
  await import('./funnel-extras.js');

  // Slug map (also expose reverse map on window for Home route to build links)
  const { SLUG_MAP, SLUG_REVERSE } = await import('./slug-map.js');
  window.SLUG_MAP = SLUG_MAP;
  window.SLUG_REVERSE_MAP = SLUG_REVERSE;

  // Components self-register on window: EhrChat, Mechanism, Brandbar, LandingPage, CampaignHQ, TweaksPanel
  // Mark + ThemeToggle MUST load BEFORE Landing.jsx — Brandbar reads both off window at
  // module scope, so a later import leaves it holding undefined.
  await import('./components/Mark.jsx');
  await import('./components/ThemeToggle.jsx');
  await import('./components/EhrChat.jsx');
  await import('./components/Mechanism.jsx');
  await import('./components/Landing.jsx');     // also defines window.Brandbar
  await import('./components/CampaignHQ.jsx');
  await import('./components/TweaksPanel.jsx');
  await import('./components/SiteFooter.jsx');

  // Route pages self-register on window: MdHome, MdInvestor, MdSecurity, MdNotFound
  //
  // ⛔ There is no public /pricing page (DEC-0188, DEC-0538): each tenant is priced through its own
  // MSA, and healthcare buyers expect that pricing to stay private. The route falls through to 404.
  //
  // ⛔ /admin AND /auth/callback WERE DELETED 2026-08-30 (DEC-0374, owner: "Delete both now").
  // Both were STUBS that shipped on the PUBLIC site: /admin rendered "Lead dashboard
  // (placeholder)… Phase 2 will wire Supabase Auth + the leads table", and /auth/callback said
  // "STUB ONLY". ⚠️ They advertised an unbuilt admin surface to anyone who guessed the URL,
  // and named an auth provider this product does not use.
  // ⭐ THE WORK THEY PROMISED NOW EXISTS ELSEWHERE AND ON THE RIGHT SURFACE: leads go to the
  // STAFF CONSOLE at /staff/leads (DEC-0377), which is the vendor plane. A lead dashboard on
  // the marketing host would have been the wrong surface even once built.
  await import('./routes/Home.jsx');
  await import('./routes/Investor.jsx');
  await import('./routes/Security.jsx');
  await import('./routes/Contact.jsx');
  await import('./routes/Privacy.jsx');
  await import('./routes/Terms.jsx');
  await import('./routes/NotFound.jsx');

  function LandingRoute() {
    const { funnel: slug } = useParams();
    const navigate = useNavigate();
    const entry = SLUG_MAP[slug];
    if (!entry) return <window.MdNotFound />;
    const ctx = window.getFunnel(entry.workplaceId, entry.avatar);
    if (!ctx) return <window.MdNotFound />;
    const LandingPage = window.LandingPage;
    return (
      <LandingPage
        funnel={ctx.funnel}
        workplace={ctx.workplace}
        /* ⛔ THE URL SLUG, PASSED EXPLICITLY — do not let the form re-derive it.
           An earlier draft composed the attribution slug as `${workplaceId}--${avatar}`
           (the shape `ALL_PAGES` uses internally), which produced 'hospital-nursing--A'
           while the visitor's actual URL is '/hospital-nurse'. ⚠️ The lead would have
           stored a slug that CANNOT BE JOINED BACK to the page that converted —
           attribution that looks captured and answers nothing. SLUG_MAP's KEY is the
           only string a marketing report can match against ad targeting. */
        slug={slug}
        tweaks={{ density: 'comfortable', font: 'default' }}
        onOptIn={(values) => {
          sessionStorage.setItem(`hw-submission-${slug}`, JSON.stringify(values));
          navigate(`/${slug}/hq`);
        }}
      />
    );
  }

  function HqRoute() {
    const { funnel: slug } = useParams();
    const entry = SLUG_MAP[slug];
    if (!entry) return <window.MdNotFound />;
    const ctx = window.getFunnel(entry.workplaceId, entry.avatar);
    if (!ctx) return <window.MdNotFound />;
    const submission = JSON.parse(sessionStorage.getItem(`hw-submission-${slug}`) || '{}');
    const CampaignHQ = window.CampaignHQ;
    return (
      <CampaignHQ
        funnel={ctx.funnel}
        workplace={ctx.workplace}
        submission={submission}
        /* ⭐ T-0007: the count the server returned with this request (DEC-0511 (2)); absent ⇒ no count is shown. */
        requestCount={Number.isInteger(submission.requestCount) ? submission.requestCount : null}
        /* ⭐ The slug builds the share link — this campaign's own landing page, a route that exists
           (Site Review - MS - FINAL M8b: "The share link is a route that exists"). */
        slug={slug}
      />
    );
  }

  function App() {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/"             element={<window.MdHome />} />
          <Route path="/investor"     element={<window.MdInvestor />} />
          <Route path="/security"     element={<window.MdSecurity />} />
          <Route path="/contact"      element={<window.MdContact />} />
          <Route path="/privacy"      element={<window.MdPrivacy />} />
          <Route path="/terms"        element={<window.MdTerms />} />
          <Route path="/:funnel"      element={<LandingRoute />} />
          <Route path="/:funnel/hq"   element={<HqRoute />} />
          <Route path="*"             element={<window.MdNotFound />} />
        </Routes>
      </BrowserRouter>
    );
  }

  ReactDOM.createRoot(document.getElementById('root')).render(<App />);
}

boot().catch((err) => {
  console.error('Boot failed:', err);
  document.getElementById('root').innerHTML = `<div style="padding:2rem;color:#b8431f;font-family:Inter">Boot failed: ${err.message}</div>`;
});
